import { patchObject } from '@synapex/tools/utils';
import { SnakeIdObj } from '@synapex/tools/typescript';
import { pickDto } from '@/shared/api';
import { AdvertiserAccount } from '@synapex/shared2/gateway-rest-api';

import {
  updateWorkspaceUser,
  deleteWorkspaceUser,
  updateWorkspaceOwnerSettings,
} from '../../api-client/users';
import { workspaceUsers } from '../key-fn';
import {
  MutateOpts,
  UseMutationOpts,
  useMutationOptimistic,
} from '../../../engine';
import { WorkspaceDescriptor } from '@synapex/shared2/entities';

///////////////

export type UpdateWorkspaceOwnerSettingsOpts = MutateOpts<
  typeof updateWorkspaceOwnerSettings,
  typeof workspaceUsers
>;

export const useUpdateWorkspaceOwnerSettings = (
  p: WorkspaceDescriptor,
  opts?: UseMutationOpts<UpdateWorkspaceOwnerSettingsOpts>,
) => {
  const queryKey = workspaceUsers(p).queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [
        queryKey[0],
        queryKey[1],
        queryKey[2],
        queryKey[3],
        queryKey[4],
      ],
      mutationFn: updateWorkspaceOwnerSettings(p),
      optimistic: (data, variables) => ({
        ...data,
        ownerSettings: {
          ...patchObject(
            data.ownerSettings,
            pickDto(AdvertiserAccount.OwnerSettings.UpdateInputDto, variables),
          ),
        },
      }),
    },
    opts,
  );
};

///////////////

export type UpdateWorkspaceUserOpts = MutateOpts<
  typeof updateWorkspaceUser,
  typeof workspaceUsers
>;

export const useUpdateWorkspaceUser = (
  p: SnakeIdObj<'user'> & WorkspaceDescriptor,
  opts?: UseMutationOpts<UpdateWorkspaceUserOpts>,
) => {
  const queryKey = workspaceUsers(p).queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [
        queryKey[0],
        queryKey[1],
        queryKey[2],
        queryKey[3],
        queryKey[4],
        p.user_id,
      ],
      mutationFn: updateWorkspaceUser(p),
      optimistic: (data, variables) => ({
        ...data,
        users: data.users.map((user) => ({
          ...user,
          permission: {
            ...patchObject(
              user.permission,
              pickDto(
                AdvertiserAccount.User.UpdatePermissionInputDto,
                variables,
              ),
            ),
          },
        })),
      }),
    },
    opts,
  );
};

///////////////

export type DeleteWorkspaceUserOpts = MutateOpts<
  typeof deleteWorkspaceUser,
  typeof workspaceUsers
>;

export const useDeleteWorkspaceUser = (
  p: SnakeIdObj<'user'> & WorkspaceDescriptor,
  opts?: UseMutationOpts<DeleteWorkspaceUserOpts>,
) => {
  const queryKey = workspaceUsers(p).queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [
        queryKey[0],
        queryKey[1],
        queryKey[2],
        queryKey[3],
        queryKey[4],
        p.user_id,
      ],
      mutationFn: deleteWorkspaceUser(p),
      optimistic: (data) => ({
        ...data,
        users: data.users.filter((user) => user.user_id !== p.user_id),
      }),
    },
    opts,
  );
};
